
import { supabase } from '@/integrations/supabase/client';

export interface HighlightVideo {
  id?: string;
  player_id: string;
  user_type: 'supabase' | 'internal';
  video_url: string;
  video_title?: string;
  display_order: number;
}

export interface PhotoGallery {
  id?: string;
  player_id: string;
  user_type: 'supabase' | 'internal';
  photo_url: string;
  photo_caption?: string;
  display_order: number;
}

export const playerMediaService = {
  // Highlight Videos
  async saveHighlightVideos(playerId: string, userType: 'supabase' | 'internal', videoUrls: string[]) {
    try {
      console.log('Saving highlight videos for player:', playerId, 'Type:', userType);
      console.log('Video URLs:', videoUrls);
      
      // First, delete existing videos for this player
      const { error: deleteError } = await supabase
        .from('player_highlight_videos')
        .delete()
        .eq('player_id', playerId)
        .eq('user_type', userType);

      if (deleteError) {
        console.error('Error deleting existing videos:', deleteError);
        return { success: false, error: deleteError.message };
      }

      // Insert new videos (only non-empty URLs)
      const videosToInsert = videoUrls
        .filter(url => url && url.trim() !== '')
        .map((url, index) => ({
          player_id: playerId,
          user_type: userType,
          video_url: url.trim(),
          display_order: index
        }));

      console.log('Videos to insert:', videosToInsert);

      if (videosToInsert.length > 0) {
        const { data, error } = await supabase
          .from('player_highlight_videos')
          .insert(videosToInsert)
          .select();

        if (error) {
          console.error('Error saving highlight videos:', error);
          return { success: false, error: error.message };
        }

        console.log('Highlight videos saved successfully:', data);
        return { success: true, data };
      }

      console.log('No videos to save, returning empty success');
      return { success: true, data: [] };
    } catch (error) {
      console.error('Error in saveHighlightVideos:', error);
      return { success: false, error: 'Failed to save highlight videos' };
    }
  },

  async getHighlightVideos(playerId: string, userType: 'supabase' | 'internal') {
    try {
      console.log('Fetching highlight videos for player:', playerId, 'Type:', userType);
      
      const { data, error } = await supabase
        .from('player_highlight_videos')
        .select('*')
        .eq('player_id', playerId)
        .eq('user_type', userType)
        .order('display_order');

      if (error) {
        console.error('Error fetching highlight videos:', error);
        return { success: false, error: error.message };
      }

      console.log('Fetched highlight videos:', data);
      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error in getHighlightVideos:', error);
      return { success: false, error: 'Failed to fetch highlight videos' };
    }
  },

  // Photo Gallery
  async savePhotoGallery(playerId: string, userType: 'supabase' | 'internal', photoUrls: string[]) {
    try {
      console.log('Saving photo gallery for player:', playerId, 'Type:', userType);
      console.log('Photo URLs:', photoUrls);
      
      // First, delete existing photos for this player
      const { error: deleteError } = await supabase
        .from('player_photo_gallery')
        .delete()
        .eq('player_id', playerId)
        .eq('user_type', userType);

      if (deleteError) {
        console.error('Error deleting existing photos:', deleteError);
        return { success: false, error: deleteError.message };
      }

      // Insert new photos (only non-empty URLs)
      const photosToInsert = photoUrls
        .filter(url => url && url.trim() !== '')
        .map((url, index) => ({
          player_id: playerId,
          user_type: userType,
          photo_url: url.trim(),
          display_order: index
        }));

      console.log('Photos to insert:', photosToInsert);

      if (photosToInsert.length > 0) {
        const { data, error } = await supabase
          .from('player_photo_gallery')
          .insert(photosToInsert)
          .select();

        if (error) {
          console.error('Error saving photo gallery:', error);
          return { success: false, error: error.message };
        }

        console.log('Photo gallery saved successfully:', data);
        return { success: true, data };
      }

      console.log('No photos to save, returning empty success');
      return { success: true, data: [] };
    } catch (error) {
      console.error('Error in savePhotoGallery:', error);
      return { success: false, error: 'Failed to save photo gallery' };
    }
  },

  async getPhotoGallery(playerId: string, userType: 'supabase' | 'internal') {
    try {
      console.log('Fetching photo gallery for player:', playerId, 'Type:', userType);
      
      const { data, error } = await supabase
        .from('player_photo_gallery')
        .select('*')
        .eq('player_id', playerId)
        .eq('user_type', userType)
        .order('display_order');

      if (error) {
        console.error('Error fetching photo gallery:', error);
        return { success: false, error: error.message };
      }

      console.log('Fetched photo gallery:', data);
      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error in getPhotoGallery:', error);
      return { success: false, error: 'Failed to fetch photo gallery' };
    }
  }
};
